import { Truncate } from "@components/Truncate";
import { invenioConfig } from "@config";
import DocumentAuthors from "@modules/Document/DocumentAuthors";
import DocumentLanguages from "@modules/Document/DocumentLanguages";
import LiteratureCover from "@modules/Literature/LiteratureCover";
import LiteratureTags from "@modules/Literature/LiteratureTags";
import LiteratureTitle from "@modules/Literature/LiteratureTitle";
import { FrontSiteRoutes } from "@routes/urls";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React from "react";
import { Link } from "react-router-dom";
import Overridable from "react-overridable";
import { Grid, Item, List } from "semantic-ui-react";
import { renderSubtitle } from "@modules/Document/utils";

const getAvailabilityColor = (availableCopies, isOverbooked) => {
  if (availableCopies > 0) return isOverbooked ? "blue" : "green";
  return "red";
};

const EItemTypeLabels = ({ eitems }) => {
  if (eitems.total === 0) return null;

  const uniqueTypes = [
    ...new Set(eitems.hits.map((eitem) => eitem.eitem_type)),
  ];

  return uniqueTypes.map((tag) => (
    <List.Item key={tag}>
      <List.Icon name="desktop" />
      <List.Content>{tag}</List.Content>
    </List.Item>
  ));
};

EItemTypeLabels.propTypes = {
  eitems: PropTypes.shape({
    total: PropTypes.number,
    hits: PropTypes.array,
  }).isRequired,
};

const CirculationInfo = ({ circulation, eitems }) => {
  const availableCopies = circulation?.available_items_for_loan_count || 0;
  const isOverbooked = circulation?.overbooked || false;
  const color = getAvailabilityColor(availableCopies, isOverbooked);

  if (!circulation && eitems.total === 0) return null;

  return (
    <List className="document-availability-wrapper">
      {circulation && (
        <List.Item>
          <List.Icon
            color={color}
            name={availableCopies > 0 ? "check" : "times"}
          />
          <List.Content>
            {availableCopies} {availableCopies === 1 ? "copy" : "copies"}{" "}
            available for loan
            {isOverbooked && availableCopies > 0 && " Add to waiting list"}
          </List.Content>
        </List.Item>
      )}
      <EItemTypeLabels eitems={eitems} />
    </List>
  );
};

CirculationInfo.propTypes = {
  circulation: PropTypes.shape({
    available_items_for_loan_count: PropTypes.number,
    overbooked: PropTypes.bool,
  }),
  eitems: PropTypes.shape({
    total: PropTypes.number,
    hits: PropTypes.array,
  }).isRequired,
};

const ImprintInfo = ({ imprint }) => {
  if (_isEmpty(imprint)) return null;

  return (
    <List>
      <List.Item>
        <List.Content>
          <span>Published by </span>
          {imprint.publisher}
        </List.Content>
      </List.Item>
      <List.Item>
        {imprint.place}, {imprint.date}
      </List.Item>
    </List>
  );
};

ImprintInfo.propTypes = {
  imprint: PropTypes.shape({
    publisher: PropTypes.string,
    place: PropTypes.string,
    date: PropTypes.string,
  }),
};

const VolumeInfo = ({ metadata }) => {
  const volume = _get(metadata, "relations.multipart_monograph[0].volume");
  return volume ? <>Volume: {volume}</> : null;
};

VolumeInfo.propTypes = {
  metadata: PropTypes.object.isRequired,
};

const EditionLanguageInfo = ({ edition, languages }) => (
  <List>
    {edition && (
      <List.Item>
        <List.Content>
          <span>Edition: </span>
          {edition}
        </List.Content>
      </List.Item>
    )}
    {languages && (
      <List.Item>
        <List.Content>
          <span>Languages: </span>
          <DocumentLanguages languages={languages} />
        </List.Content>
      </List.Item>
    )}
  </List>
);

EditionLanguageInfo.propTypes = {
  edition: PropTypes.string,
  languages: PropTypes.array,
};

const DocumentListEntry = ({ metadata }) => {
  const detailsUrl = FrontSiteRoutes.documentDetailsFor(metadata.pid);
  const isRestricted = _get(metadata, "restricted", false);
  const coverUrl = _get(metadata, "cover_metadata.urls.medium");

  return (
    <Item>
      <Item.Image floated="left" as={Link} to={detailsUrl}>
        <LiteratureCover
          isRestricted={isRestricted}
          size="small"
          url={coverUrl}
        />
      </Item.Image>

      <Item.Content>
        <Item.Meta>{metadata.document_type}</Item.Meta>

        <Item.Header as={Link} to={detailsUrl}>
          <LiteratureTitle title={metadata.title} />
        </Item.Header>

        <Overridable id="DocumentListEntry.BeforeAuthors" metadata={metadata} />

        <Item.Meta className="document-authors">
          {renderSubtitle(metadata?.alternative_titles)}
          <DocumentAuthors
            authors={metadata.authors}
            hasOtherAuthors={metadata.other_authors}
            prefix="by "
            limit={invenioConfig.LITERATURE.authors.maxDisplay}
          />
        </Item.Meta>

        <Item.Description>
          <Truncate lines={2}>{metadata.abstract}</Truncate>
        </Item.Description>

        <Item.Meta>
          <Grid columns="equal">
            <Grid.Column>
              <CirculationInfo
                circulation={metadata.circulation}
                eitems={metadata.eitems}
              />
            </Grid.Column>

            <Grid.Column>
              <ImprintInfo imprint={metadata.imprint} />
            </Grid.Column>

            <Grid.Column>
              <EditionLanguageInfo
                edition={metadata.edition}
                languages={metadata.languages}
              />
            </Grid.Column>

            <Grid.Column>
              <VolumeInfo metadata={metadata} />
            </Grid.Column>
          </Grid>
        </Item.Meta>

        <Item.Extra>
          <LiteratureTags tags={metadata.tags} />
        </Item.Extra>
      </Item.Content>
    </Item>
  );
};

DocumentListEntry.propTypes = {
  metadata: PropTypes.object.isRequired,
};

export default Overridable.component("DocumentListEntry", DocumentListEntry);
